import React, {
  useContext, useRef,
} from 'react';

import {
  Typography, Box, Form, useWidth,
  InputField, Button, Grid, Link,
} from 'vindi-ds-components';
import api from '@services/api';

import vindiLogo from '@assets/images/admin/logo-vindi.png';
import ErrorsAlert from '@components/errors_alert';
import ReCAPTCHA from 'react-google-recaptcha';
import templates from '../templates.json';
import styles from './styles';
import WarningText from './warning_text';
import buildValidations from './validations';
import { FormContext } from '../hooks/useFormData';
import TermOfUseCheckbox from '../components/term_of_use_checkbox';

function buildSignUpParams(values) {
  const queryParameters = new URLSearchParams(window.location.search);

  const utmParams = {
    utm_source: queryParameters.get('utm_source'),
    utm_medium: queryParameters.get('utm_medium'),
    utm_campaign: queryParameters.get('utm_campaign'),
    utm_term: queryParameters.get('utm_term'),
    utm_content: queryParameters.get('utm_content'),
    fbcl_id: queryParameters.get('fbclid'),
    gcl_id: queryParameters.get('GCLID'),
  };

  return {
    sign_up: {
      ...values,
      ...utmParams,
    },
  };
}

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]).replace(/\+/g, ' ') : '';
}

function initFormParams() {
  return {

    registry_code: '',
    company_name: '',
    user_name: getCookie('now_name'),
    user_email: getCookie('now_email'),
    phone_number: getCookie('now_phone'),
  };
}

const ValidationSchema = buildValidations(templates);

const ALPHANUMERIC_CHAR = /[0-9A-Za-z]/;
const CNPJ_MASK = [
  ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, '.',
  ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, '.',
  ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, '/',
  ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, ALPHANUMERIC_CHAR, '-',
  /\d/, /\d/,
];

const fields = [
  {
    name: 'registry_code',
    breakpoint: 12,
    props: {
      mask: CNPJ_MASK,
    },
  },
  { name: 'user_name', breakpoint: 12 },
  { name: 'user_email', breakpoint: 12, props: { type: 'email' } },
  { name: 'phone_number', breakpoint: 12, props: { mask: 'cell' } },
  { name: 'company_name', breakpoint: 12, props: { helperText: 'O nome que você constuma se comunicar para seus clientes' } },
];

function FormBox({ onSuccessSubmit }) {
  const formValues = initFormParams();
  const { useOfTerms, errors, setErrors } = useContext(FormContext);
  const styleButton = ['xs', 'sm', 'md'].includes(useWidth()) ? styles.buttonMobile : styles.button;
  const GOOGLE_RECAPTCHA_SITE_KEY = process.env.RECAPTCHA_SITE_KEY;
  const captchaRef = useRef(null);

  function handleError(error) {
    const responseData = error.response.data;
    const errorsArray = [];

    Object.entries(responseData).forEach(([field, value]) => {
      const fieldName = templates.form.fields[field];
      errorsArray.push(`${fieldName} ${value.join(', ')}`);
    });

    setErrors(errorsArray);
  }

  async function handleSubmit(values, token) {
    const signUpParams = buildSignUpParams(values);
    signUpParams.sign_up.token_captcha = token;
    signUpParams.sign_up.assign_terms_of_plan = useOfTerms;
    try {
      const response = await api.post('/users', signUpParams);
      onSuccessSubmit(response.data.user);
    } catch (error) {
      handleError(error);
    }
  }

  return (
    <Box css={styles.formBox} maxWidth={{ sm: '420px', lg: '512px' }}>
      <img src={vindiLogo} alt='Logo Vindi' style={styles.vindiLogo} />
      <div style={styles.formHeading}>
        <Typography variant='h4' component='h2'>{templates.form.title}</Typography>
      </div>
      <ErrorsAlert
        style={styles.errorsAlert}
        title={templates.messages.error.alert}
        errors={errors}
      />
      <Form
        initialValues={formValues}
        onSubmit={(values) => handleSubmit(values, captchaRef.current.getValue())}
        validationSchema={ValidationSchema}
      >
        <Grid container spacing={4}>
          {fields.map(({ name, breakpoint, props }) => (
            <Grid item key={name} lg={breakpoint} sm={12} xs={12} md={12}>
              <InputField
                name={name}
                label={templates.form.required_fields[name]}
                placeholder={templates.form.placeholders[name]}
                {...props}
              />
            </Grid>
          ))}
          <TermOfUseCheckbox />
        </Grid>
        <Box display='flex' flexDirection='column'>
          <WarningText />
          <ReCAPTCHA
            ref={captchaRef}
            sitekey={GOOGLE_RECAPTCHA_SITE_KEY}
            style={styles.recaptcha}
          />
          <Button type='submit' style={styleButton}>Criar conta</Button>
          <Typography variant='body2'>Já possui uma conta? <Link href='/users/sign_in' style={styles.signInLink}>Fazer login</Link></Typography>
        </Box>
      </Form>
    </Box>
  );
}

export default FormBox;
